// src/pages/dashboard/AdminProducts.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import {
  getProductsPublic,
  createProductAdmin,
  updateProductAdmin,
  deleteProductAdmin,
} from "../../core/api/productsApi";

const STATUS_OPTIONS = [
  { value: "LIVE", label: "Live" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "COMING_SOON", label: "Coming Soon" },
];

const emptyForm = {
  name: "",
  tagline: "",
  app_type: "",
  short_description: "",
  description: "",
  status: "LIVE",
  status_label: "Live",
  featuresText: "",
  cta_label: "",
  cta_url: "",
  website_url: "",
  android_app_url: "",
  ios_app_url: "",
  is_featured: false,
  sort_order: 1,
  is_active: true,
};

const toFeaturesArray = (featuresText) =>
  String(featuresText || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

// ✅ SAME MODAL AS EXPENSES (PORTAL)
function Modal({ open, title, onClose, children, footer }) {
  useEffect(() => {
    if (!open) return;

    // lock body scroll + prevent layout shift when scrollbar disappears
    const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = document.body.style.overflow;
    const prevPaddingRight = document.body.style.paddingRight;

    document.body.style.overflow = "hidden";
    if (scrollBarWidth > 0) document.body.style.paddingRight = `${scrollBarWidth}px`;

    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPaddingRight;
    };
  }, [open]);

  if (!open) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[99999] bg-black/60">
      {/* overlay click closes */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* modal wrapper */}
      <div className="relative z-[100000] flex min-h-screen items-center justify-center p-4">
        <div
          className="w-full max-w-3xl rounded-xl bg-white shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b p-4">
            <h3 className="text-lg font-semibold">{title}</h3>
            <button
              onClick={onClose}
              className="rounded-md px-3 py-1 text-sm hover:bg-gray-100"
            >
              ✕
            </button>
          </div>

          {/* Body */}
          <div className="p-4 max-h-[70vh] overflow-y-auto">{children}</div>

          {/* Footer */}
          {footer ? <div className="border-t p-4">{footer}</div> : null}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function AdminProducts() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Modal state
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState("create"); // create | edit
  const [activeId, setActiveId] = useState(null);

  const [form, setForm] = useState({ ...emptyForm });
  const [coverFile, setCoverFile] = useState(null);

  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ type: "", text: "" });

  // scroll to top inside modal body (optional)
  const topRef = useRef(null);

  const scrollToTop = () => {
    setTimeout(() => {
      topRef.current?.scrollIntoView({ behavior: "auto", block: "start" });
    }, 0);
  };

  const load = async () => {
    try {
      setLoading(true);
      setErr("");
      const data = await getProductsPublic();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Failed to load products");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999));
  }, [rows]);

  const openCreate = () => {
    setMode("create");
    setActiveId(null);
    setForm({ ...emptyForm });
    setCoverFile(null);
    setToast({ type: "", text: "" });
    setOpen(true);
    scrollToTop();
  };

  const openEdit = (p) => {
    setMode("edit");
    setActiveId(p.id);
    setForm({
      ...emptyForm,
      name: p.name ?? "",
      tagline: p.tagline ?? "",
      app_type: p.app_type ?? "",
      short_description: p.short_description ?? "",
      description: p.description ?? "",
      status: p.status ?? "LIVE",
      status_label: p.status_label ?? "",
      featuresText: Array.isArray(p.features) ? p.features.join("\n") : "",
      cta_label: p.cta_label ?? "",
      cta_url: p.cta_url ?? "",
      website_url: p.website_url ?? "",
      android_app_url: p.android_app_url ?? "",
      ios_app_url: p.ios_app_url ?? "",
      is_featured: Boolean(p.is_featured),
      sort_order: p.sort_order ?? 1,
      is_active: p.is_active ?? true,
    });
    setCoverFile(null);
    setToast({ type: "", text: "" });
    setOpen(true);
    scrollToTop();
  };

  const close = () => {
    if (saving) return;
    setOpen(false);
  };

  const setField = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
    setToast({ type: "", text: "" });
  };

  const save = async () => {
    setToast({ type: "", text: "" });

    if (!form.name.trim()) {
      setToast({ type: "error", text: "Name is required" });
      scrollToTop();
      return;
    }

    const payload = {
      name: form.name.trim(),
      tagline: form.tagline.trim(),
      app_type: form.app_type.trim(),
      short_description: form.short_description.trim(),
      description: form.description.trim(),
      status: form.status,
      status_label: form.status_label.trim(),
      features: toFeaturesArray(form.featuresText),
      cta_label: form.cta_label.trim(),
      cta_url: form.cta_url.trim(),
      website_url: form.website_url.trim(),
      android_app_url: form.android_app_url.trim(),
      ios_app_url: form.ios_app_url.trim(),
      is_featured: Boolean(form.is_featured),
      sort_order: Number(form.sort_order || 0),
      is_active: Boolean(form.is_active),
    };

    try {
      setSaving(true);
      if (mode === "create") {
        await createProductAdmin(payload, coverFile);
      } else {
        await updateProductAdmin(activeId, payload, coverFile);
      }
      await load();
      setOpen(false);
    } catch (e2) {
      const apiMsg =
        e2?.response?.data?.detail ||
        (typeof e2?.response?.data === "string" ? e2.response.data : null) ||
        e2?.message ||
        "Save failed";
      setToast({ type: "error", text: apiMsg });
      scrollToTop();
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (p) => {
    const ok = window.confirm(`Delete product: ${p.name}? (Soft delete)`);
    if (!ok) return;
    try {
      await deleteProductAdmin(p.id);
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Delete failed");
    }
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Products</h1>
          <p className="text-sm text-gray-600 mt-1">Manage website products (CRUD)</p>
        </div>

        <button
          onClick={openCreate}
          className="rounded-xl bg-gray-900 text-white px-4 py-2 text-sm font-semibold hover:bg-gray-800"
        >
          + Add Product
        </button>
      </div>

      <div className="mt-5">
        {loading && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 text-gray-600">
            Loading…
          </div>
        )}

        {!loading && err && (
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-red-700">
            {err}
          </div>
        )}

        {!loading && !err && sorted.length === 0 && (
          <div className="rounded-2xl border border-gray-200 bg-white p-6 text-gray-600">
            No products found.
          </div>
        )}

        {!loading && !err && sorted.length > 0 && (
          <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-700">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Name</th>
                  <th className="text-left px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold">Featured</th>
                  <th className="text-left px-4 py-3 font-semibold">Sort</th>
                  <th className="text-left px-4 py-3 font-semibold">CTA</th>
                  <th className="text-right px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-100">
                {sorted.map((p) => (
                  <tr key={p.id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-900">{p.name}</div>
                      <div className="text-xs text-gray-600 line-clamp-1">
                        {p.short_description || "—"}
                      </div>
                    </td>

                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs border border-gray-200 bg-gray-100 text-gray-700">
                        {p.status_label || p.status || "—"}
                      </span>
                    </td>

                    <td className="px-4 py-3">
                      {p.is_featured ? (
                        <span className="text-xs px-2 py-1 rounded-full bg-green-50 text-green-700 border border-green-200">
                          Yes
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-1 rounded-full bg-gray-50 text-gray-600 border border-gray-200">
                          No
                        </span>
                      )}
                    </td>

                    <td className="px-4 py-3">{p.sort_order ?? "—"}</td>

                    <td className="px-4 py-3">
                      {p.cta_url ? (
                        <a
                          href={p.cta_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-gray-900 underline underline-offset-2"
                        >
                          Open
                        </a>
                      ) : (
                        <span className="text-gray-500">—</span>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEdit(p)}
                          className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold hover:bg-gray-50"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => onDelete(p)}
                          className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-100"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ✅ PORTAL MODAL (SAME AS EXPENSES) */}
      <Modal
        open={open}
        title={mode === "create" ? "Add Product" : "Edit Product"}
        onClose={close}
        footer={
          <div className="flex justify-end gap-2">
            <button
              className="rounded-lg border px-4 py-2 text-sm"
              onClick={close}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              className="rounded-lg bg-black px-4 py-2 text-sm text-white hover:bg-gray-800"
              onClick={save}
              disabled={saving}
            >
              {saving ? "Saving..." : mode === "create" ? "Create" : "Update"}
            </button>
          </div>
        }
      >
        <div ref={topRef} />

        {toast.text ? (
          <div
            className={[
              "mb-4 rounded-xl border p-3 text-sm",
              toast.type === "success"
                ? "border-green-200 bg-green-50 text-green-800"
                : "border-red-200 bg-red-50 text-red-800",
            ].join(" ")}
          >
            {toast.text}
          </div>
        ) : null}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-gray-700">Name *</label>
            <input
              value={form.name}
              onChange={(e) => setField("name", e.target.value)}
              className="mt-1 w-full rounded-lg border p-2 text-sm"
              placeholder="Ledger Book"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">Tagline</label>
            <input
              value={form.tagline}
              onChange={(e) => setField("tagline", e.target.value)}
              className="mt-1 w-full rounded-lg border p-2 text-sm"
              placeholder="Fast, simple, reliable"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">App Type</label>
            <input
              value={form.app_type}
              onChange={(e) => setField("app_type", e.target.value)}
              className="mt-1 w-full rounded-lg border p-2 text-sm"
              placeholder="DESKTOP / WEB / MOBILE"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">Status</label>
            <select
              value={form.status}
              onChange={(e) => {
                const st = e.target.value;
                setField("status", st);
                const found = STATUS_OPTIONS.find((x) => x.value === st);
                if (found && !form.status_label.trim()) setField("status_label", found.label);
              }}
              className="mt-1 w-full rounded-lg border p-2 text-sm bg-white"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="text-xs font-semibold text-gray-700">Status Label</label>
            <input
              value={form.status_label}
              onChange={(e) => setField("status_label", e.target.value)}
              className="mt-1 w-full rounded-lg border p-2 text-sm"
              placeholder="Live / Coming Soon"
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-xs font-semibold text-gray-700">Short Description</label>
            <input
              value={form.short_description}
              onChange={(e) => setField("short_description", e.target.value)}
              className="mt-1 w-full rounded-lg border p-2 text-sm"
              placeholder="Desktop + dashboard ledger system"
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-xs font-semibold text-gray-700">Long Description</label>
            <textarea
              rows={4}
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              className="mt-1 w-full rounded-lg border p-2 text-sm"
              placeholder="Write full details..."
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-xs font-semibold text-gray-700">Features (one per line)</label>
            <textarea
              rows={4}
              value={form.featuresText}
              onChange={(e) => setField("featuresText", e.target.value)}
              className="mt-1 w-full rounded-lg border p-2 text-sm"
              placeholder={"Daily entries\nPDF bills\nDashboard reports"}
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">CTA Label</label>
            <input
              value={form.cta_label}
              onChange={(e) => setField("cta_label", e.target.value)}
              className="mt-1 w-full rounded-lg border p-2 text-sm"
              placeholder="Open Live"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">CTA URL</label>
            <input
              value={form.cta_url}
              onChange={(e) => setField("cta_url", e.target.value)}
              className="mt-1 w-full rounded-lg border p-2 text-sm"
              placeholder="https://mockingjay.tech/ledger"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">Website URL</label>
            <input
              value={form.website_url}
              onChange={(e) => setField("website_url", e.target.value)}
              className="mt-1 w-full rounded-lg border p-2 text-sm"
              placeholder="https://mockingjay.tech/ledger"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">Android App URL</label>
            <input
              value={form.android_app_url}
              onChange={(e) => setField("android_app_url", e.target.value)}
              className="mt-1 w-full rounded-lg border p-2 text-sm"
              placeholder="(optional)"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">iOS App URL</label>
            <input
              value={form.ios_app_url}
              onChange={(e) => setField("ios_app_url", e.target.value)}
              className="mt-1 w-full rounded-lg border p-2 text-sm"
              placeholder="(optional)"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-700">Sort Order</label>
            <input
              type="number"
              value={form.sort_order}
              onChange={(e) => setField("sort_order", e.target.value)}
              className="mt-1 w-full rounded-lg border p-2 text-sm"
            />
          </div>

          <div className="md:col-span-2">
            <label className="text-xs font-semibold text-gray-700">Cover Image (optional)</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => setCoverFile(e.target.files?.[0] || null)}
              className="mt-1 w-full rounded-lg border p-2 text-sm bg-white"
            />
            <p className="mt-1 text-xs text-gray-500">
              If you upload a cover, API uses multipart/form-data.
            </p>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="featured"
              type="checkbox"
              checked={form.is_featured}
              onChange={(e) => setField("is_featured", e.target.checked)}
            />
            <label htmlFor="featured" className="text-sm text-gray-700">
              Featured
            </label>
          </div>

          <div className="flex items-center gap-2">
            <input
              id="active"
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setField("is_active", e.target.checked)}
            />
            <label htmlFor="active" className="text-sm text-gray-700">
              Active
            </label>
          </div>
        </div>
      </Modal>
    </div>
  );
}
